import {createBrowserRouter, Link, RouterProvider} from "react-router";
import HomePage from "../pages/home-page";
import Layout from "../layout/layout";
import useCatalogRoutes from "../pages/catalog/useCatalogRoutes.tsx";
import SignUp from "../pages/sign-up";
import SignIn from "../pages/sign-in";
import Wishlist from "../pages/wishlist";
import {CartPage} from "../pages/cart";
import {CheckoutPage} from "../pages/checkout";
import FaqPage from "../pages/faq/page";
import { SimplePage } from "../pages/simple-page";
import { SimplePageType } from "../types/index";
import AboutUsPage from "../pages/about-us";
import {
    AccountLayout,
    AccountPersonalInformation,
    AccountCart,
    AccountOrders,
    AccountWishlist,
    AccountCoupons,
    AccountSupport,
    AccountSettings
} from "../pages/account";
import { useSimplePage } from "../pages/simple-page/useSimplePage.ts";
import { CheckoutSuccessPage } from "../pages/checkout/page-success.tsx";
import AccountChangePassword from "../pages/account/change-password.tsx";
import { PasswordReset } from "../pages/password-reset/reset.tsx";
import { PasswordResetConfirm } from "../pages/password-reset/confirm.tsx";
import { useMemo } from "react";
import MarketplaceUpload from "../pages/marketplace-upload/index.tsx";
import { MARKETPLACE_CONFIG, MARKETPLACE2_CONFIG } from "../pages/marketplace-upload/config.ts";
import { ErrorPage } from "../pages/error/error-page.tsx";

export const AppRouter = () => {
    const catalogRoutes = useCatalogRoutes();
    const { findPage } = useSimplePage();

    // Only memoize layout as it's the parent of all routes
    const layout = useMemo(() => <Layout/>, []);
    
    // Memoize the router configuration to prevent recreation on each render
    const router = useMemo(() => createBrowserRouter([
        {
            element: layout,
            errorElement: <ErrorPage />,
            children: [
                {
                    index: true,
                    element: <HomePage/>,
                    handle: {
                        title: 'Home'
                    }
                },
                ...catalogRoutes,
                {
                    path: 'sign-up',
                    element: <SignUp/>,
                    loader: () => ({}),
                    handle: {
                        title: 'Sign Up',
                        crumb: () => <Link to="/sign-up">Sign Up</Link>
                    },
                },
                {
                    path: 'sign-in',
                    element: <SignIn/>,
                    loader: () => ({}),
                    handle: {
                        title: 'Sign In',
                        crumb: () => <Link to="/sign-in">Sign In</Link>
                    },
                },
                {
                    path: 'password-reset',
                    element: <PasswordReset/>,
                    loader: () => ({}),
                    handle: {
                        title: 'Password Reset',
                        crumb: () => <Link to="/password-reset">Password Reset</Link>
                    },
                },
                {
                    path: 'password-reset/:token',
                    element: <PasswordResetConfirm/>,
                    loader: () => ({}),
                    handle: {
                        title: 'Password Reset',
                        crumb: () => [
                            <Link to="/password-reset">Password Reset</Link>,
                            <Link to={`/password-reset/:token`}>Confirm</Link>
                        ]
                    },
                },
                {
                    path: 'wishlist',
                    element: <Wishlist/>,
                    loader: () => ({}),
                    handle: {
                        title: 'Wishlist',
                        crumb: () => <Link to="/wishlist">Wishlist</Link>
                    },
                },
                {
                    path: 'cart',
                    element: <CartPage/>,
                    handle: {
                        title: 'Shopping Cart',
                        crumb: () => <Link to="/cart">My cart</Link>
                    },
                },
                {
                    path: 'checkout',
                    element: <CheckoutPage/>,
                    handle: {
                        title: 'Checkout',
                        crumb: () => <Link to="/checkout">Checkout</Link>
                    },
                },
                {
                    path: 'checkout/success/:orderId',
                    element: <CheckoutSuccessPage/>,
                    handle: {
                        title: 'Checkout Success',
                        crumb: () => <Link to="/account/orders">Checkout Success</Link>
                    },
                },
                {
                    path: 'faq',
                    element: <FaqPage/>,
                    handle: {
                        title: 'FAQ',
                        crumb: () => <Link to="/faq">FAQ</Link>
                    },
                },
                {
                    path: 'about-us',
                    element: <AboutUsPage/>,
                    handle: {
                        title: 'About Us',
                        crumb: () => <Link to="/about-us">About Us</Link>
                    },
                },
                {
                    path: 'pages/:slug',
                    element: <SimplePage/>,
                    loader: async ({ params }) => {
                        const { slug } = params;
                        if (!slug) {
                            throw new Response("Not Found", { status: 404 });
                        }
                        const page = await findPage(slug);
                        if (!page) {
                            throw new Response("Not Found", { status: 404 });
                        }
                        return {...page, slug: slug};
                    },
                    handle: {
                        title: ({data}:  { data: SimplePageType }) => data.title,
                        crumb: ({ data }: { data: SimplePageType }) => {
                            return <Link to={`/pages/${data.slug}`}>{data.shortTitle || data.title}</Link>
                        }
                    },
                },
                {
                    path: 'upload',
                    element: <MarketplaceUpload config={MARKETPLACE_CONFIG}/>,
                    handle: {
                        title: 'Marketplace Upload',
                        crumb: () => <Link to="/upload">Upload images</Link>
                    }
                },{
                    path: 'uploader',
                    element: <MarketplaceUpload config={MARKETPLACE2_CONFIG}/>,
                    handle: {
                        title: 'Marketplace Upload',
                        crumb: () => <Link to="/uploader">Marketplace Upload</Link>
                    }
                },
                {
                    path: 'account',
                    element: <AccountLayout/>,
                    errorElement: <ErrorPage />,
                    handle: {
                        title: 'Account'
                    },
                    children: [
                        {
                            index: true,
                            element: <AccountPersonalInformation/>,
                            handle: {
                                title: 'Personal Information',
                                crumb: () => <Link to="/account">Personal Information</Link>
                            }
                        },
                        {
                            path: 'cart',
                            element: <AccountCart/>,
                            handle: {
                                title: 'My Cart',
                                crumb: () => <Link to="/account/cart">Cart</Link>
                            }
                        },
                        {
                            path: 'orders',
                            element: <AccountOrders/>,
                            handle: {
                                title: 'My Orders',
                                crumb: () => <Link to="/account/orders">Orders</Link>
                            }
                        },
                        {
                            path: 'wishlist',
                            element: <AccountWishlist/>,
                            handle: {
                                title: 'My Wishlist',
                                crumb: () => <Link to="/account/wishlist">Wishlist</Link>
                            }
                        },
                        {
                            path: 'coupons',
                            element: <AccountCoupons/>,
                            handle: {
                                title: 'My Coupons',
                                crumb: () => <Link to="/account/coupons">Coupons</Link>
                            }
                        },
                        {
                            path: 'support',
                            element: <AccountSupport/>,
                            handle: {
                                title: 'Support',
                                crumb: () => <Link to="/account/support">Support</Link>
                            }
                        },
                        {
                            path: 'settings',
                            element: <AccountSettings/>,
                            handle: {
                                title: 'Account Settings',
                                crumb: () => <Link to="/account/settings">Settings</Link>
                            }
                        },
                        {
                            path: 'change-password',
                            element: <AccountChangePassword/>,
                            handle: {
                                title: 'Change Password',
                                crumb: () => <Link to="/account/change-password">Change Password</Link>
                            }
                        }
                    ]
                },
                {
                    path: '*',
                    element: <ErrorPage />,
                }
            ],
        },
    ]), [layout, catalogRoutes, findPage]);

    return <RouterProvider router={router} />;
};

export default AppRouter;